import { creatorbase, creatorbaseQueries } from '@round/api';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, UIEventHandler } from 'react';
import { debounce } from 'lodash';
import { mapClientPayeeToOption } from './usePayeeSelect';

const queryOptions = creatorbaseQueries.clientPayee.options.infinite;

type Params = {
    params?: Pick<creatorbase.GetClientPayeesParams, 'client_id' | 'is_payable'>;
    enabled?: boolean;
};

const LIST_SCROLL_THRESHOLD_PX = 80;

export function usePayeeCombobox({ params, enabled = true }: Params = {}) {
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const setDebouncedSearchDebounced = useMemo(() => debounce((value: string) => setDebouncedSearch(value), 500), []);

    useEffect(() => {
        return () => {
            setDebouncedSearchDebounced.cancel();
        };
    }, [setDebouncedSearchDebounced]);

    const queryEnabled = enabled && params?.client_id != null;

    const { data, isFetching, hasNextPage, fetchNextPage } = useInfiniteQuery({
        ...queryOptions({ search: debouncedSearch, page_size: 25, ...params }),
        enabled: queryEnabled,
    });

    const items = useMemo(() => data?.pages.flatMap((page) => page.results.map(mapClientPayeeToOption)) ?? [], [data]);

    const onInputValueChange = useCallback(
        (value: string) => {
            setDebouncedSearchDebounced(value);
        },
        [setDebouncedSearchDebounced]
    );

    const onItemsListScroll = useCallback<UIEventHandler<HTMLDivElement>>(
        (event) => {
            const el = event.currentTarget;
            if (el.scrollHeight - el.scrollTop - el.clientHeight > LIST_SCROLL_THRESHOLD_PX) {
                return;
            }
            if (hasNextPage && !isFetching) {
                void fetchNextPage();
            }
        },
        [fetchNextPage, hasNextPage, isFetching]
    );

    return {
        items,
        isFetching,
        /** Disables Base UI client-side filtering; list is driven by API `search` via debounced query. */
        filter: null,
        onInputValueChange,
        onItemsListScroll,
    };
}
